import { inject, Service, signal, Type } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterStateSnapshot,
  Routes,
  RoutesRecognized
} from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { SetActiveArea } from '@mng/mng-frame';
import { adminServicesRoute } from './admin/admin-services.route';
import { auditlogRoute } from './auditlog/auditlog.route';
import { abrechnungenRoute } from './leistung/abrechnungen/abrechnungen.route';
import { cmsContentRoute } from './cms/cms-content.route';
import { dienstleistungDemoRoute } from './dienstleistung/dienstleistung-demo/dienstleistung-demo.route';
import { dokumenteRoute } from './dokumente/dokumente.route';
import { fallpartnerDetailRoute } from './fall/fallpartnerdetail/fallpartnerdetail.route';
import { kundeListRoute } from './kunde/kunde-list/kunde-list.route';
import { leistungRoute } from './leistung/leistung/leistung.route';
import { leistungdefRoute } from './leistung/leistungdef-list/leistungdef.route';
import { leistungImporterRoute } from './leistung/leistung-importer/leistung-importer.route';
import { partnerDetailRoute } from './partner/partner-detail/partner-detail.route';
import { partnerRoute } from './partner/partner.route';
import {
  passwordAenderungRoute,
  passwortAenderungMitIdUndPasswortHashRoute
} from './passwort/passwort-aenderung/passwort-aenderung.route';
import { programmeRoute } from './programm/programme.route';
import { userRoute } from './user/user.route';
import { userListRoute } from './user/user-list-container/user-list-container.route';
import { kidsPotentielleRoute } from './kids/kids-potentielle/kids-potentielle.route';
import { kidsTerminRoute } from './kids/kids-termine/kids-termin/kids-termin.route';
import { kidsTermineRoute } from './kids/kids-termine/kids-termine.route';
import { kidsBuchungRoute } from './kids-buchung/kids-buchung.route';
import { kidsHotelRoute } from './kids/kids-hotel/kids-hotel.route';
import { kidsImportRoute } from './kids/kids-import/kids-import.route';
import { AuthStateFacade } from './auth/auth-state.facade';
import { codePruefungRoute } from './dienstleistung/code-pruefung/code-pruefung.route';
import { dienstleistungAdminBearbeitungRoute } from './dienstleistung/dienstleistung-admin-bearbeitung/dienstleistung-admin-bearbeitung.route';
import { dienstleistungListRoute } from './dienstleistung/dienstleistung-list/dienstleistung-list.route';
import { dienstleistungRoute } from './dienstleistung/dienstleistung.route';
import { dienstleistungLadenRoute } from './dienstleistung/dienstleistung-laden/dienstleistung-laden.route';
import { dienstleistungSchnellabschlussRoute } from './dienstleistung/dienstleistung-schnellabschluss/dienstleistung-schnellabschluss.route';
import { dienstleistungErfassungRoute } from './dienstleistung/dienstleistung-erfassung/dienstleistung-erfassung.route';
import { laborProtokollRoute } from './labor/labor-protokoll/labor-protokoll.route';
import { laborProtokollDetailRoute } from './labor/labor-protokoll/laborprotokoll-detail/laborprotokoll-detail.route';

export const routes: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./home/home.component')
  },
  {
    path: '',
    loadComponent: () => import('./home/home.component')
  },
  adminServicesRoute,
  auditlogRoute,
  abrechnungenRoute,
  cmsContentRoute,
  codePruefungRoute,
  dienstleistungDemoRoute,
  dienstleistungAdminBearbeitungRoute,
  dienstleistungListRoute,
  dienstleistungRoute,
  dienstleistungLadenRoute,
  dienstleistungSchnellabschlussRoute,
  dienstleistungErfassungRoute,
  dokumenteRoute,
  fallpartnerDetailRoute,
  kundeListRoute,
  laborProtokollRoute,
  laborProtokollDetailRoute,
  leistungRoute,
  leistungdefRoute,
  leistungImporterRoute,
  partnerDetailRoute,
  partnerRoute,
  passwortAenderungMitIdUndPasswortHashRoute,
  passwordAenderungRoute,
  programmeRoute,
  userRoute,
  userListRoute,
  kidsPotentielleRoute,
  kidsTerminRoute,
  kidsTermineRoute,
  kidsBuchungRoute,
  kidsHotelRoute,
  kidsImportRoute
];

export interface RouteInfo {
  params?: {};
  queryParams?: {};
  component?: Type<any>;
  url?: string;
}

@Service()
export class MgsRoutingService {
  readonly router = inject(Router);
  readonly authState = inject(AuthStateFacade);
  readonly store = inject(Store);
  readonly activatedRoute = inject(ActivatedRoute);

  routeTitle = signal('');
  routeInfo = signal<RouteInfo>({});
  initialize() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        tap((route) => {
          try {
            const snapshot: ActivatedRouteSnapshot = route.snapshot;
            const state: RouterStateSnapshot = this.router.routerState.snapshot;
            const routeInfo: RouteInfo = {
              component: snapshot.component,
              params: snapshot.params,
              queryParams: snapshot.queryParams,
              url: state?.url
            };
            this.routeInfo.set(routeInfo);
          } catch (e) {
            this.routeInfo.set({});
            console.error(e);
          }
        }),
        mergeMap((route) => route['title'])
      )
      .subscribe((routeTitle) => {
        const title = `MGS ${routeTitle ?? ''}`;
        this.routeTitle.set(routeTitle ?? '');

        setTimeout(() => this.store.dispatch(new SetActiveArea({ title })));
      });

    // Authentication Check für alle Routen

    this.router.events
      .pipe(filter((event) => event instanceof RoutesRecognized))
      .subscribe((event: RoutesRecognized) => {
        const url = event.urlAfterRedirects;

        // const isPublicRoute = url === '/' || url.startsWith('/passwort') || url.startsWith(`/${kidsBuchungRoute.path}`);
        // console.log('isPublicRoute', url, isPublicRoute);

        if (url === '/' && this.authState.authenticated() && this.authState.isKidsTerminUser()) {
          void this.router.navigate([`/${kidsBuchungRoute.path}`]);
        }

        /* if (!isPublicRoute && !this.authState.authenticated()) {
        console.log('!!!! Unauthentifizierter Zugriff auf ', url, `Es wird zu '/' navigiert `);
        void this.router.navigate(['/']);
      } */
      });
  }
}
